import type { Component } from 'solid-js';
import { Router, Route } from '@solidjs/router';

import Home from './pages/Home';
import Security from './pages/Security';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import AccountDeletion from './pages/AccountDeletion';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Mybots from './pages/Mybots';
import { Provider } from './components/UserContextProvider';
import Account from './pages/Account';
import VerifySession from './pages/VerifySession';
import VerifyAccount from './pages/VerifyAccount';
import Admin from './pages/Admin';
import TenantAdmin from './pages/TenantAdmin';
import AdminNewTenant from './pages/AdminNewTenant';
import AdminTenantDetails from './pages/AdminTenantDetails';
import NewTenant from './pages/NewTenant';
import NewTenantMember from './pages/NewTenantMember';
import MyCommands from './pages/MyCommands';
import MyGrants from './pages/MyGrants';
import { ToastProvider } from './components/MessageBox';
import { UserPage } from './pages/User';
import { GrantPage } from './pages/Grant';
import { ActivateMinecraftSession } from './pages/ActivateMinecraftSession';
import { Bot } from './pages/Bot';
import { ChatLogPage } from './pages/ChatLogPage';
import Messages from './pages/Messages';
import Voting from './pages/Voting';
import AdminVoting from './pages/AdminVoting';
import AdminVotingProxies from './pages/AdminVotingProxies';
import AdminAlerts from './pages/AdminAlerts';
import AdminPlayerBlacklist from './pages/AdminPlayerBlacklist';
import AdminBotshopDomains from './pages/AdminBotshopDomains';
import AdminCompressedPlacement from './pages/AdminCompressedPlacement';
import SelfShutoffReport from './pages/SelfShutoffReport';
import AdminReconnectWindows from './pages/AdminReconnectWindows';
import CitybuildStatusPage from './pages/CitybuildStatus';
import AdminLicenses from './pages/AdminLicenses';
import MyLicenses from './pages/MyLicenses';
import { AllItems } from './pages/items/AllItems';
import { Item } from './pages/items/Item';
import { NewItem } from './pages/items/NewItem';
import { AuditLogs } from './pages/AuditLogs';
import ActivityFeed from './pages/ActivityFeed';
import { CommandPage } from './pages/Command';
import { DownloadsPage } from './pages/Downloads';
import Goggles from './pages/Goggles';
import Koffermatic from './pages/Koffermatic';
import VektorUtils from './pages/VektorUtils';
import { TranslateLog } from './pages/TranslateLog';
import MyOAuthClients from './pages/OAuthClients';
import OAuthClientPage from './pages/OAuthClient';
import OAuthAuthorize from './pages/OAuthAuthorize';
import AccountSwitcher from './pages/AccountSwitcher';
import Jobs from './pages/Jobs';
import JobDetails from './pages/JobDetails';
import DropConfigs from './pages/DropConfigs';
import DropConfig from './pages/DropConfig';
import NewDropConfig from './pages/NewDropConfig';
import { MSLoginPage } from './pages/MSLogin';
import Tutorial from './pages/Tutorial';
import Onboarding from './pages/Onboarding';
import AppEventView from './pages/AppEventView';
import Weihnachten from './pages/xdsm/Weihnachten';
import DsmDemo from './pages/xdsm/DsmDemo';
import Inventory from './pages/Inventory';
import InventoryCraft from './pages/InventoryCraft';
import InventoryCraftOrder from './pages/InventoryCraftOrder';
import InventoryExternalDemand from './pages/InventoryExternalDemand';
import StorageAdmin from './pages/StorageAdmin';
import InventoryAuditLog from './pages/InventoryAuditLog';
import AhBrowse from './pages/AhBrowse';
import AhWatches from './pages/AhWatches';
import AhWatch from './pages/AhWatch';
import ProxyAdmin from './pages/ProxyAdmin';
import BotRegistryAdmin from './pages/BotRegistryAdmin';
import SchematicLicenses from './pages/SchematicLicenses';
import FeatureFlagsAdmin from './pages/FeatureFlagsAdmin';
import Settings from './pages/Settings';
import ApiKeysPage from './pages/ApiKeysPage';
import Webhooks from './pages/Webhooks';
import TenantSettings from './pages/TenantSettings';
import TenantMembers from './pages/TenantMembers';
import TenantGrants from './pages/TenantGrants';
import BotEngine from './pages/BotEngine';
import Sellbot from './pages/Sellbot';
import SellbotOffers from './pages/SellbotOffers';
import SellbotOffer from './pages/SellbotOffer';
import SellbotTransactions from './pages/SellbotTransactions';
import SellbotTransactionDetail from './pages/SellbotTransactionDetail';
import SellbotActivity from './pages/SellbotActivity';
import SellbotStats from './pages/SellbotStats';
import SellbotConfig from './pages/SellbotConfig';
import SellbotSelfTest from './pages/SellbotSelfTest';
import SellbotHolograms from './pages/SellbotHolograms';
import SellbotHologram from './pages/SellbotHologram';
import SellbotGrants from './pages/SellbotGrants';
import SellbotGrant from './pages/SellbotGrant';
import Payments from './pages/Payments';
import PaymentsScheduled from './pages/PaymentsScheduled';
import PlotAcl from './pages/PlotAcl';
import PlayerLists from './pages/PlayerLists';
import FarmHelperConfig from './pages/FarmHelperConfig';
import MultiOrchestrate from './pages/MultiOrchestrate';
import BotEngineStats from './pages/BotEngineStats';
import BotEngineHistory from './pages/BotEngineHistory';
import OnlinePlayers from './pages/OnlinePlayers';
import OnlinePlayerHistory from './pages/OnlinePlayerHistory';
import UsernameTracker from './pages/UsernameTracker';
import SeedCracker from './pages/SeedCracker';
import SeedCrackerDetail from './pages/SeedCrackerDetail';
import SeedCrackerMap from './pages/SeedCrackerMap';
import BotManagement from './pages/BotManagement';
import BotAuditLog from './pages/BotAuditLog';
import VektorPossessions from './pages/VektorPossessions';
import Schematics from './pages/Schematics';
import SchematicBuild from './pages/SchematicBuild';
import MapartEditor from './pages/MapartEditor';
import MapCache from './pages/MapCache';
import AdminDiagnosticMonitors from './pages/AdminDiagnosticMonitors';
import AdminPacketRecordings from './pages/AdminPacketRecordings';
import AdminTaskCapsules from './pages/AdminTaskCapsules';
import AdminCustomItems from './pages/AdminCustomItems';
import AdminKoffermaticPerformance from './pages/AdminKoffermaticPerformance';
import AdminCaseOpeningStats from './pages/AdminCaseOpeningStats';
import BotDisconnects from './pages/BotDisconnects';
import Maintenance from './pages/Maintenance';
import BotProfileHistory from './pages/BotProfileHistory';
import BotProfile from './pages/BotProfile';
import BotManage from './pages/BotManage';
import BotManageAccess from './pages/BotManageAccess';
import BotPlugins from './pages/BotPlugins';
import PluginOverview from './pages/PluginOverview';
import ShopStock from './pages/ShopStock';
import ShopStockWatchlist from './pages/ShopStockWatchlist';
import BiomeExplorerPage from './pages/bot/BiomeExplorer';
import FarmPage from './pages/bot/Farm';
import DemolishPage from './pages/bot/Demolish';
import OrbsTraderPage from './pages/bot/OrbsTrader';
import OrbsSellPage from './pages/bot/OrbsSell';
import PokeballPage from './pages/bot/Pokeball';
import RepairPage from './pages/bot/Repair';
import CropFarmerPage from './pages/bot/CropFarmer';
import VillagerTradingPage from './pages/bot/VillagerTrading';
import CaseOpeningPage from './pages/bot/CaseOpening';
import AutomationPage from './pages/bot/Automation';
import UnloadAreaPage from './pages/bot/UnloadArea';
import SpawnerUpgradePage from './pages/bot/SpawnerUpgrade';
import SurveillancePage from './pages/bot/Surveillance';
import VotingBotPage from './pages/bot/Voting';
import { DialogHost } from './components/Dialog';
import { SkullOwnerHost } from './components/SkullOwnerView';
import { SkullOwnerPickerHost } from './components/SkullOwnerPicker';
import { ItemInfoHost } from './components/ItemInfoView';
import DashboardLayout from './layouts/DashboardLayout';
import NavbarLayout from './layouts/NavbarLayout';
import { CraftingResolverProvider } from './services/craftingResolverContext';
import { ActiveBotProvider } from './state/activeBot';
import { MessageUnreadProvider } from './state/messageUnread';
import { MutedContactsProvider } from './state/mutedContacts';
import MessageSoundListener from './components/MessageSoundListener';
import { useServiceWorkerNavigation } from './services/pwa';
import FeatureGate from './components/FeatureGate';
import LicenseGate from './components/LicenseGate';
import { GrantTypes } from './services/grantsApi';
import Text from './components/Text';

const FeatureDisabled = () => (
    <div style={{ padding: 'var(--spacing-2xl)', "max-width": '600px', margin: '0 auto', "text-align": 'center' }}>
        <h1><Text val="FEATURE_FLAGS_DISABLED_TITLE" /></h1>
        <p style={{ color: 'var(--dark-text-muted)' }}><Text val="FEATURE_FLAGS_DISABLED_BODY" /></p>
    </div>
);

const LicenseDenied = () => (
    <div style={{ padding: 'var(--spacing-2xl)', "max-width": '600px', margin: '0 auto', "text-align": 'center' }}>
        <h1><Text val="LICENSE_DENIED_TITLE" /></h1>
        <p style={{ color: 'var(--dark-text-muted)' }}><Text val="LICENSE_DENIED_BODY" /></p>
    </div>
);

const Gated = (flag: string, page: () => any) => (
    <FeatureGate flag={flag} fallback={<DashboardLayout><FeatureDisabled /></DashboardLayout>}>
        <DashboardLayout>{page()}</DashboardLayout>
    </FeatureGate>
);

const Licensed = (license: string, page: () => any) => (
    <LicenseGate type={license} fallback={<DashboardLayout><LicenseDenied /></DashboardLayout>}>
        <DashboardLayout>{page()}</DashboardLayout>
    </LicenseGate>
);

const GatedAndLicensed = (flag: string, license: string, page: () => any) => (
    <FeatureGate flag={flag} fallback={<DashboardLayout><FeatureDisabled /></DashboardLayout>}>
        <LicenseGate type={license} fallback={<DashboardLayout><LicenseDenied /></DashboardLayout>}>
            <DashboardLayout>{page()}</DashboardLayout>
        </LicenseGate>
    </FeatureGate>
);

// koffermatic.com serves this same SPA but only ever shows the mod's own download page at "/".
// Everywhere else "/" is the Vektor home. `?koffermatic` forces it on any host for local preview.
const onKoffermaticHost = (): boolean => {
  const host = window.location.hostname.toLowerCase();
  if (host === 'koffermatic.com' || host.endsWith('.koffermatic.com')) return true;
  return new URLSearchParams(window.location.search).has('koffermatic');
};

const RootRoute: Component = () =>
  onKoffermaticHost() ? <Koffermatic /> : <NavbarLayout><Home /></NavbarLayout>;

const App: Component = () => {

  return (
    <div>
      <Provider>
        <ToastProvider>
          <CraftingResolverProvider>
          <ActiveBotProvider>
          <MessageUnreadProvider>
          <MutedContactsProvider>
          <MessageSoundListener />
          <Router root={(props) => { useServiceWorkerNavigation(); return props.children; }}>
              <Route path="/maintenance" component={() => <Maintenance />} />
              <Route path="/" component={RootRoute} />
              <Route path="/koffermatic" component={() => <Koffermatic />} />
              <Route path="/security" component={() => <NavbarLayout><Security /></NavbarLayout>} />
              <Route path="/sicherheit" component={() => <NavbarLayout><Security /></NavbarLayout>} />
              <Route path="/impressum" component={() => <NavbarLayout><Impressum /></NavbarLayout>} />
              <Route path="/imprint" component={() => <NavbarLayout><Impressum /></NavbarLayout>} />
              <Route path="/datenschutz" component={() => <NavbarLayout><Datenschutz /></NavbarLayout>} />
              <Route path="/privacy" component={() => <NavbarLayout><Datenschutz /></NavbarLayout>} />
              <Route path="/account-deletion" component={() => <NavbarLayout><AccountDeletion /></NavbarLayout>} />
              <Route path="/konto-loeschen" component={() => <NavbarLayout><AccountDeletion /></NavbarLayout>} />
              <Route path="/login" component={() => <NavbarLayout><Login /></NavbarLayout>} />
              <Route path="/register" component={() => <NavbarLayout><Register /></NavbarLayout>} />
              <Route path="/me" component={() => <NavbarLayout><Account /></NavbarLayout>} />
              <Route path="/verifysession" component={() => <NavbarLayout><VerifySession /></NavbarLayout>} />
              <Route path="/verifyaccount" component={() => <NavbarLayout><VerifyAccount /></NavbarLayout>} />
              <Route path="/onboarding" component={() => <NavbarLayout><Onboarding /></NavbarLayout>} />
              <Route path="/activatesession/:code" component={() => <NavbarLayout><ActivateMinecraftSession /></NavbarLayout>} />
              <Route path="/activationdone" component={() => <NavbarLayout><div><h1>Activation done</h1><p>Du kannst dieses Fenster schließen</p></div></NavbarLayout>} />
              <Route path="/downloads" component={() => <NavbarLayout><DownloadsPage /></NavbarLayout>} />
              <Route path="/goggles" component={() => <NavbarLayout><Goggles /></NavbarLayout>} />
              <Route path="/vektorutils" component={() => <NavbarLayout><VektorUtils /></NavbarLayout>} />
              <Route path="/oauth/authorize" component={() => <NavbarLayout><OAuthAuthorize /></NavbarLayout>} />
              <Route path="/oauth/ms/callback" component={() => <NavbarLayout><MSLoginPage /></NavbarLayout>} />
              <Route path="/jobs" component={() => <NavbarLayout><Jobs /></NavbarLayout>} />
              <Route path="/jobs/:unlocalizedName/:name" component={() => <NavbarLayout><JobDetails /></NavbarLayout>} />

              <Route path="/dashboard" component={() => <DashboardLayout><Dashboard /></DashboardLayout>} />
              <Route path="/mybots" component={() => <DashboardLayout><Mybots /></DashboardLayout>} />
              <Route path="/admin" component={() => <DashboardLayout><Admin /></DashboardLayout>} />
              <Route path="/admin/tenants" component={() => <DashboardLayout><TenantAdmin /></DashboardLayout>} />
              <Route path="/admin/tenants/new" component={() => <DashboardLayout><AdminNewTenant /></DashboardLayout>} />
              <Route path="/admin/tenants/:id" component={() => <DashboardLayout><AdminTenantDetails /></DashboardLayout>} />
              <Route path="/admin/proxies" component={() => Gated('PROXIES', () => <ProxyAdmin />)} />
              <Route path="/admin/bots" component={() => <DashboardLayout><BotRegistryAdmin /></DashboardLayout>} />
              <Route path="/admin/schematic-licenses" component={() => <DashboardLayout><SchematicLicenses /></DashboardLayout>} />
              <Route path="/admin/feature-flags" component={() => <DashboardLayout><FeatureFlagsAdmin /></DashboardLayout>} />
              <Route path="/admin/possessions" component={() => GatedAndLicensed('VEKTOR_GOGGLES', GrantTypes.LicenseVektorGoggles, () => <VektorPossessions />)} />
              <Route path="/schematics" component={() => Gated('SCHEMATICS', () => <Schematics />)} />
              <Route path="/schematics/:id/build" component={() => Gated('SCHEMATICS', () => <SchematicBuild />)} />
              <Route path="/mapart" component={() => Gated('MAPART', () => <MapartEditor />)} />
              <Route path="/map-cache" component={() => Gated('MAP_CACHE_PAGE', () => <MapCache />)} />
              <Route path="/admin/goggles-monitoring" component={() => <DashboardLayout><AdminDiagnosticMonitors /></DashboardLayout>} />
              <Route path="/admin/packet-recordings" component={() => <DashboardLayout><AdminPacketRecordings /></DashboardLayout>} />
              <Route path="/admin/task-capsules" component={() => <DashboardLayout><AdminTaskCapsules /></DashboardLayout>} />
              <Route path="/admin/custom-items" component={() => <DashboardLayout><AdminCustomItems /></DashboardLayout>} />
              <Route path="/admin/koffermatic-performance" component={() => <DashboardLayout><AdminKoffermaticPerformance /></DashboardLayout>} />
              <Route path="/admin/case-stats" component={() => Gated('CASE_OPENING', () => <AdminCaseOpeningStats />)} />
              <Route path="/disconnects/bots" component={() => Gated('DETAILED_MONITORING', () => <BotDisconnects kind="bot" />)} />
              <Route path="/disconnects/possessions" component={() => Gated('DETAILED_MONITORING', () => <BotDisconnects kind="possession" />)} />
              <Route path="/mycommands" component={() => <DashboardLayout><MyCommands /></DashboardLayout>} />
              <Route path="/mygrants" component={() => <DashboardLayout><MyGrants /></DashboardLayout>} />
              <Route path="/user/:id" component={() => <DashboardLayout><UserPage /></DashboardLayout>} />
              <Route path="/grant/:id" component={() => <DashboardLayout><GrantPage /></DashboardLayout>} />
              <Route path="/bot/:id" component={() => <DashboardLayout><Bot /></DashboardLayout>} />
              <Route path="/sellbot" component={() => Licensed(GrantTypes.SellbotSeat, () => <Sellbot />)} />
              <Route path="/sellbot/offers" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotOffers />)} />
              <Route path="/sellbot/offer/:offerId" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotOffer />)} />
              <Route path="/sellbot/transactions" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotTransactions />)} />
              <Route path="/sellbot/transactions/:txnId" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotTransactionDetail />)} />
              <Route path="/sellbot/activity" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotActivity />)} />
              <Route path="/sellbot/stats" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotStats />)} />
              <Route path="/sellbot/config" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotConfig />)} />
              <Route path="/sellbot/selftest" component={() => Licensed(GrantTypes.SellbotSeat, () => <SellbotSelfTest />)} />
              <Route path="/sellbot/holograms" component={() => GatedAndLicensed('SELLBOT_HOLOGRAMS', GrantTypes.SellbotSeat, () => <SellbotHolograms />)} />
              <Route path="/sellbot/holograms/:hologramId" component={() => GatedAndLicensed('SELLBOT_HOLOGRAMS', GrantTypes.SellbotSeat, () => <SellbotHologram />)} />
              <Route path="/sellbot/grants" component={() => GatedAndLicensed('SELLBOT_GRANTS', GrantTypes.SellbotSeat, () => <SellbotGrants />)} />
              <Route path="/sellbot/grants/:grantId" component={() => GatedAndLicensed('SELLBOT_GRANTS', GrantTypes.SellbotSeat, () => <SellbotGrant />)} />
              <Route path="/payments" component={() => Gated('PAYMENTS', () => <Payments />)} />
              <Route path="/payments/scheduled" component={() => Gated('PAYMENTS', () => <PaymentsScheduled />)} />
              <Route path="/plotacl" component={() => Licensed(GrantTypes.PlotAclSeat, () => <PlotAcl />)} />
              <Route path="/playerlists" component={() => Gated('PLAYER_LIST', () => <PlayerLists />)} />
              <Route path="/farmhelper/config" component={() => Gated('FARM_HELPER', () => <FarmHelperConfig />)} />
              <Route path="/chatlogs" component={() => <DashboardLayout><ChatLogPage /></DashboardLayout>} />
              <Route path="/messages" component={() => Gated('MSGS', () => <Messages />)} />
              <Route path="/items" component={() => <DashboardLayout><AllItems /></DashboardLayout>} />
              <Route path="/items/:id" component={() => <DashboardLayout><Item /></DashboardLayout>} />
              <Route path="/items/new" component={() => <DashboardLayout><NewItem /></DashboardLayout>} />
              <Route path="/auditlogs" component={() => <DashboardLayout><AuditLogs /></DashboardLayout>} />
              <Route path="/activity" component={() => <DashboardLayout><ActivityFeed /></DashboardLayout>} />
              <Route path="/auditlogs/:id" component={() => <DashboardLayout><AuditLogs /></DashboardLayout>} />
              <Route path="/command/:id" component={() => <DashboardLayout><CommandPage /></DashboardLayout>} />
              <Route path="/log" component={() => <DashboardLayout><TranslateLog /></DashboardLayout>} />
              <Route path="/oauthclients" component={() => <DashboardLayout><MyOAuthClients /></DashboardLayout>} />
              <Route path="/oauthclient/:id" component={() => <DashboardLayout><OAuthClientPage /></DashboardLayout>} />
              <Route path="/dropconfigs" component={() => <DashboardLayout><DropConfigs /></DashboardLayout>} />
              <Route path="/dropconfigs/new" component={() => <DashboardLayout><NewDropConfig /></DashboardLayout>} />
              <Route path="/dropconfigs/:id" component={() => <DashboardLayout><DropConfig /></DashboardLayout>} />
              <Route path="/tutorial" component={() => <DashboardLayout><Tutorial /></DashboardLayout>} />
              <Route path="/eventviewer" component={() => <DashboardLayout><AppEventView /></DashboardLayout>} />
              <Route path="/inventory" component={() => Gated('INVENTORY_PAGE', () => <Inventory />)} />
              <Route path="/inventory/craft" component={() => Gated('CRAFTING', () => <InventoryCraft />)} />
              <Route path="/inventory/craft/orders" component={() => Gated('CRAFT_ORDERS', () => <InventoryCraftOrder />)} />
              <Route path="/inventory/demand" component={() => Gated('STOCK_PLANNING', () => (
                <FeatureGate flag="CRAFT_ORDERS" fallback={<FeatureDisabled />}>
                  <FeatureGate flag="CRAFTING" fallback={<FeatureDisabled />}><InventoryExternalDemand /></FeatureGate>
                </FeatureGate>
              ))} />
              <Route path="/inventory/storages" component={() => Gated('INVENTORY_PAGE', () => <StorageAdmin />)} />
              <Route path="/inventory/audit" component={() => Gated('INVENTORY_AUDIT', () => <InventoryAuditLog />)} />
              <Route path="/ah" component={() => Gated('AH_HISTORY', () => <AhBrowse />)} />
              <Route path="/ah/watches" component={() => Gated('AH_HISTORY', () => <AhWatches />)} />
              <Route path="/ah/watches/:watchId" component={() => Gated('AH_HISTORY', () => <AhWatch />)} />
              <Route path="/bots" component={() => <DashboardLayout><BotManagement /></DashboardLayout>} />
              <Route path="/bots/:id/manage" component={() => <DashboardLayout><BotManage /></DashboardLayout>} />
              <Route path="/bots/:id/manage/access" component={() => <DashboardLayout><BotManageAccess /></DashboardLayout>} />
              <Route path="/bots/:id/auditlog" component={() => <DashboardLayout><BotAuditLog /></DashboardLayout>} />
              <Route path="/bots/:id/profile-history" component={() => <DashboardLayout><BotProfileHistory /></DashboardLayout>} />
              <Route path="/bots/:id/profile" component={() => <DashboardLayout><BotProfile /></DashboardLayout>} />
              <Route path="/botengine" component={() => <DashboardLayout><BotEngine /></DashboardLayout>} />
              <Route path="/botengine/stats" component={() => <DashboardLayout><BotEngineStats /></DashboardLayout>} />
              <Route path="/botengine/history" component={() => <DashboardLayout><BotEngineHistory /></DashboardLayout>} />
              <Route path="/multi" component={() => Gated('MULTI_ORCHESTRATE', () => <MultiOrchestrate />)} />
              <Route path="/online-players" component={() => <DashboardLayout><OnlinePlayers /></DashboardLayout>} />
              <Route path="/online-players/:domain/:name" component={() => <DashboardLayout><OnlinePlayerHistory /></DashboardLayout>} />
              <Route path="/username-tracker" component={() => <DashboardLayout><UsernameTracker /></DashboardLayout>} />
              <Route path="/seed-cracker" component={() => Gated('SEED_CRACKER_PAGE', () => <SeedCracker />)} />
              <Route path="/seed-cracker/world" component={() => Gated('SEED_CRACKER_PAGE', () => <SeedCrackerDetail />)} />
              <Route path="/seed-cracker/map" component={() => Gated('SEED_CRACKER_PAGE', () => <SeedCrackerMap />)} />
              <Route path="/botplugins" component={() => <DashboardLayout><BotPlugins /></DashboardLayout>} />
              <Route path="/plugins/overview" component={() => <DashboardLayout><PluginOverview /></DashboardLayout>} />
              <Route path="/shopstock" component={() => GatedAndLicensed('SHOP_STOCK', GrantTypes.LicenseShopStock, () => <ShopStock />)} />
              <Route path="/shopstock/watchlist" component={() => GatedAndLicensed('SHOP_STOCK_WATCHLIST', GrantTypes.LicenseShopStock, () => <ShopStockWatchlist />)} />
              <Route path="/voting" component={() => <DashboardLayout><Voting /></DashboardLayout>} />
              <Route path="/bot/voting" component={() => Gated('VOTING', () => <VotingBotPage />)} />
              <Route path="/bot/farm" component={() => Gated('FARM', () => <FarmPage />)} />
              <Route path="/bot/demolish" component={() => Gated('DEMOLISH', () => <DemolishPage />)} />
              <Route path="/bot/orbs-trader" component={() => Gated('ORBS_TRADER', () => <OrbsTraderPage />)} />
              <Route path="/bot/orbs-trader/sell" component={() => Gated('ORBS_TRADER', () => <OrbsSellPage />)} />
              <Route path="/bot/pokeball" component={() => Gated('POKEBALL', () => <PokeballPage />)} />
              <Route path="/bot/repair" component={() => Gated('REPAIR', () => <RepairPage />)} />
              <Route path="/bot/crop-farmer" component={() => Gated('CROP_FARMER', () => <CropFarmerPage />)} />
              <Route path="/bot/villager-trading" component={() => Gated('VILLAGER_TRADING', () => <VillagerTradingPage />)} />
              <Route path="/bot/case-opening" component={() => Gated('CASE_OPENING', () => <CaseOpeningPage />)} />
              <Route path="/bot/automation" component={() => Gated('AUTOMATION', () => <AutomationPage />)} />
              <Route path="/bot/unloadarea" component={() => Gated('UNLOAD_AREA', () => <UnloadAreaPage />)} />
              <Route path="/bot/spawnerupgrade" component={() => Gated('SPAWNER_UPGRADE', () => <SpawnerUpgradePage />)} />
              <Route path="/bot/surveillance" component={() => Gated('SURVEILLANCE', () => <SurveillancePage />)} />
              <Route path="/bot/biome-explorer" component={() => <DashboardLayout><BiomeExplorerPage /></DashboardLayout>} />
              <Route path="/admin/voting" component={() => <DashboardLayout><AdminVoting /></DashboardLayout>} />
              <Route path="/admin/voting/proxies" component={() => <DashboardLayout><AdminVotingProxies /></DashboardLayout>} />
              <Route path="/admin/alerts" component={() => <DashboardLayout><AdminAlerts /></DashboardLayout>} />
              <Route path="/admin/blacklist" component={() => Gated('SELF_SHUTOFF', () => <AdminPlayerBlacklist />)} />
              <Route path="/admin/botshop-domains" component={() => <DashboardLayout><AdminBotshopDomains /></DashboardLayout>} />
              <Route path="/admin/compressed-placement" component={() => <DashboardLayout><AdminCompressedPlacement /></DashboardLayout>} />
              <Route path="/selfshutoff/:id" component={() => Gated('SELF_SHUTOFF', () => <SelfShutoffReport />)} />
              <Route path="/admin/reconnect-windows" component={() => <DashboardLayout><AdminReconnectWindows /></DashboardLayout>} />
              <Route path="/admin/citybuild-status" component={() => Gated('CITYBUILD_STATUS', () => <CitybuildStatusPage />)} />
              <Route path="/admin/licenses" component={() => <DashboardLayout><AdminLicenses /></DashboardLayout>} />
              <Route path="/admin/tenant/:id/grants" component={() => <DashboardLayout><TenantGrants /></DashboardLayout>} />
              <Route path="/mylicenses" component={() => <DashboardLayout><MyLicenses /></DashboardLayout>} />
              <Route path="/settings" component={() => <DashboardLayout><Settings /></DashboardLayout>} />
              <Route path="/apikeys" component={() => <DashboardLayout><ApiKeysPage /></DashboardLayout>} />
              <Route path="/webhooks" component={() => Gated('AUTOMATION', () => <Webhooks />)} />
              <Route path="/tenant/settings" component={() => <DashboardLayout><TenantSettings /></DashboardLayout>} />
              <Route path="/tenant/new" component={() => <DashboardLayout><NewTenant /></DashboardLayout>} />
              <Route path="/tenant/members" component={() => Gated('TEAM_MANAGEMENT', () => <TenantMembers />)} />
              <Route path="/tenant/members/new" component={() => Gated('TEAM_MANAGEMENT', () => <NewTenantMember />)} />
              <Route path="/tenant/grants" component={() => <DashboardLayout><TenantGrants /></DashboardLayout>} />

              <Route path="/account-switcher" component={() => <AccountSwitcher />} />
              <Route path="/xdsm" component={() => <Weihnachten />} />
              <Route path="/dsmdemo" component={() => <DsmDemo />} />
          </Router>
          </MutedContactsProvider>
          </MessageUnreadProvider>
          </ActiveBotProvider>
          </CraftingResolverProvider>
        </ToastProvider>
        <DialogHost />
        <SkullOwnerHost />
        <SkullOwnerPickerHost />
        <ItemInfoHost />
      </Provider>
    </div>
  );
};

export default App;
